import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Checkbox,
  ListItemText,
  CircularProgress,
  Autocomplete,
  Paper,
  Divider,
  IconButton
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation } from "react-router-dom";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";
import AppToast from "../../components/AppToast";
import useFormDraft from "../../hooks/useFormDraft";

const filter = (options, params) => {
  const filtered = options.filter((option) => {
    const inputValue = params.inputValue.toLowerCase();
    return option.projectName.toLowerCase().includes(inputValue);
  });

  const { inputValue } = params;
  // Suggest the creation of a new value
  const isExisting = options.some((option) => inputValue.toLowerCase() === option.projectName.toLowerCase());
  if (inputValue !== '' && !isExisting) {
    filtered.push({
      projectName: inputValue,
      isNew: true,
    });
  }

  return filtered;
};

const AddNewProject = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const projectToEdit = location.state?.project;
  const firRequestToApprove = location.state?.firRequestToApprove;
  const isFreshCreation = location.state?.isFreshCreation;
  const isEditMode = !!projectToEdit;
  const { token } = useAuth?.() || {};
  const firRequestId = location.state?.firRequestId || firRequestToApprove?._id || "";

  const [formData, setFormData, clearDraft] = useFormDraft("draft_new_project", {
    projectName: projectToEdit?.projectName || (firRequestToApprove ? (firRequestToApprove.propertyName || firRequestToApprove.buildingName || firRequestToApprove.projectName || "") : ""),
    address: projectToEdit?.address || (firRequestToApprove ? `${firRequestToApprove.fullAddress || ""}, ${firRequestToApprove.city || ""}, ${firRequestToApprove.state || ""} - ${firRequestToApprove.pincode || ""}`.replace(/^[,\s]+|[,\s]+$/g, '') : ""),
    projectStatus: projectToEdit?.projectStatus || "inprogress",
    clientName: projectToEdit?.clientName || firRequestToApprove?.fullName || firRequestToApprove?.clientName || "",
    companyName: projectToEdit?.companyName || (firRequestToApprove ? firRequestToApprove.builderName : ""),
    projectManager: projectToEdit?.projectManager || "",
    contactNumber: projectToEdit?.contactNumber || firRequestToApprove?.mobileNumber || firRequestToApprove?.contactNumber || "",
    assignedEngineers: projectToEdit?.assignedEngineers?.map(eng => typeof eng === 'string' ? eng : eng._id) || [],
    architect: projectToEdit?.architect || "",
    structuralConsultant: projectToEdit?.structuralConsultant || "",
    mepConsultant: projectToEdit?.mepConsultant || "",
    interiorDesigner: projectToEdit?.interiorDesigner || "",
    individual: projectToEdit?.individual || "",
    otherConsultant: projectToEdit?.otherConsultant || "",
    towers: projectToEdit?.towers || [],
    source: projectToEdit?.source || (firRequestId ? "FIR" : ""),
    firStatus: projectToEdit?.firStatus || (firRequestId ? "Project Mapped" : ""),
    propertyType: projectToEdit?.propertyType || firRequestToApprove?.propertyType || "",
    firRequestId: firRequestId
  }, !isEditMode && !firRequestToApprove && !isFreshCreation);

  const [masterMappings, setMasterMappings] = useState([]);

  const [engineersList, setEngineersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [openEngineerMenu, setOpenEngineerMenu] = useState(false);

  const [searchableProjects, setSearchableProjects] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  useEffect(() => {
    fetchSearchableProjects();
    fetchMasterMappings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMasterMappings = async () => {
    try {
      const response = await axios.get(`${url}/api/master-mappings/all`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      if (response.data.success) {
        setMasterMappings(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching master mappings:", error);
    }
  };

  const fetchSearchableProjects = async () => {
    setIsSearchLoading(true);
    try {
      const response = await axios.get(`${url}/api/master-projects/searchable?exclude=standard`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });
      if (response.data.success) {
        setSearchableProjects(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching searchable projects:", error);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleProjectSelect = (event, newValue) => {
    if (!newValue) {
      setFormData(prev => ({ ...prev, projectName: "" }));
      return;
    }

    const { projectName, address, clientName, companyName, contactNumber, source, isNew } = newValue;

    if (isNew) {
      setFormData(prev => ({ ...prev, projectName: projectName }));
      return;
    }

    // Pre-fill relatable fields
    setFormData(prev => ({
      ...prev,
      projectName: projectName || "",
      address: address || prev.address,
      // Skip clientName pre-fill for Site Visit projects as requested
      clientName: source === 'Site Visit' ? prev.clientName : (clientName || prev.clientName),
      // Skip contactNumber pre-fill for Site Visit and Inhouse projects as requested
      contactNumber: (source === 'Site Visit' || source === 'Inhouse') ? prev.contactNumber : (contactNumber || prev.contactNumber),
      // Pre-fill developer name for all sources (mapped to companyName)
      companyName: companyName || prev.companyName,
    }));
  };

  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: ""
  });

  useEffect(() => {
    fetchApprovedEngineers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchApprovedEngineers = async () => {
    try {
      const response = await axios.get(`${url}/api/users/fetch`, {
        params: {
          role: "ENGINEER",
          status: "APPROVED"
        },
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined
        }
      });

      if (response.data && response.data.users) {
        setEngineersList(response.data.users);
      }
    } catch (error) {
      console.error("Error fetching engineers:", error);
      showToast("Failed to fetch engineers list", "error");
    } finally {
      setFetchLoading(false);
    }
  };

  const showToast = (message, type = "success") => {
    setToast({
      open: true,
      type,
      title: type === "success" ? "Success" : "Error",
      message
    });
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const handleChange = (field, value) => {
    if (field === "contactNumber") {
      // Numbers only and max 10 digits
      const cleaned = value.replace(/\D/g, "");
      if (cleaned.length <= 10) {
        setFormData((prev) => ({ ...prev, [field]: cleaned }));
      }
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEngineerChange = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      assignedEngineers: typeof value === "string" ? value.split(",") : value
    }));
    setOpenEngineerMenu(false); // Close dropdown immediately after selection
  };

  const handleDelete = (id) => {
    setFormData((prev) => ({
      ...prev,
      assignedEngineers: prev.assignedEngineers.filter((e) => e !== id)
    }));
  };

  const handleAddTower = () => {
    setFormData((prev) => ({
      ...prev,
      towers: [...(prev.towers || []), { name: "", projectType: "", customTypeName: "" }]
    }));
  };

  const handleTowerChange = (index, field, value) => {
    const updatedTowers = [...(formData.towers || [])];
    updatedTowers[index][field] = value;
    setFormData((prev) => ({ ...prev, towers: updatedTowers }));
  };

  const handleRemoveTower = (index) => {
    const updatedTowers = [...(formData.towers || [])];
    updatedTowers.splice(index, 1);
    setFormData((prev) => ({ ...prev, towers: updatedTowers }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.projectName || !formData.address || !formData.clientName) {
      showToast("Please fill all required fields (*)", "error");
      return;
    }

    setLoading(true);
    try {
      const apiUrl = isEditMode
        ? `${url}/api/projects/update/${projectToEdit._id}`
        : `${url}/api/projects/create`;

      const method = isEditMode ? 'put' : 'post';

      const response = await axios({
        method,
        url: apiUrl,
        data: formData,
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json"
        }
      });

      if (response.data.success) {
        if (!isEditMode) clearDraft();
        showToast(isEditMode ? "Project updated successfully!" : "Project created successfully!", "success");
        setTimeout(() => {
          if (isEditMode) {
            if (formData.source === "FIR") {
              navigate("/projects/fir-requests");
            } else {
              navigate("/projects/all");
            }
          } else {
            navigate(`/technical/start-mapping/${response.data.data._id}`);
          }
        }, 1500);
      }
    } catch (error) {
      console.error("Error creating project:", error);
      showToast(error.response?.data?.message || "Failed to create project", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- Input Style ---------------- */

  const inputStyle = {
    width: "100%",
    maxWidth: "321px",

    "& .MuiOutlinedInput-root": {
      height: "50px",
      borderRadius: "7px",

      "& fieldset": {
        borderWidth: "1px",
        borderColor: COLORS.GREY_COLOR
      },

      "&:hover fieldset": {
        borderColor: COLORS.SECONDARY
      },

      "&.Mui-focused fieldset": {
        borderColor: COLORS.SECONDARY
      },
      "& input": {
        color: COLORS.PRIMARY,
        fontFamily: FONT_FAMILY
      }
    },

    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      padding: "0 14px",
      height: "50px",
      boxSizing: "border-box"
    }
  };

  const autoGrowStyle = {
    ...inputStyle,
    "& .MuiOutlinedInput-root": {
      ...inputStyle["& .MuiOutlinedInput-root"],
      height: "auto",
      minHeight: "50px",
      padding: "13px 14px", // Adjusted padding for exact 50px initial height
      alignItems: "center",
      "& textarea": {
        padding: "0",
        color: COLORS.PRIMARY,
        fontFamily: FONT_FAMILY
      }
    }
  };

  const labelStyle = {
    fontFamily: FONT_FAMILY,
    ...TYPOGRAPHY.H4,
    color: COLORS.PRIMARY,
    mb: 1,
    display: "block"
  };

  /* ---------------- Dropdown Menu Style ---------------- */

  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 200,
        overflowY: "auto",
        marginTop: "4px"
      }
    },
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left"
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left"
    }
  };

  /* ---------------- Main Container ---------------- */

  const mainContainerStyle = {
    p: 1,

  };

  /* ---------------- Row Container ---------------- */

  const rowContainerStyle = {
    display: "grid",
    gridTemplateColumns: {
      xs: "100%",
      sm: "repeat(2, 1fr)",
      md: "repeat(3, 1fr)"
    },
    gap: "24px",
    mb: "28px",
    width: "100%"
  };

  /* ---------------- Column Container ---------------- */

  const columnContainerStyle = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0 // Prevents grid items from overflowing when content is too wide
  };

  /* ---------------- Section Paper Style ---------------- */
  const sectionPaperStyle = {
    p: { xs: 2, md: 4 },
    mb: 4,
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.04)",
    backgroundColor: "#fff",
    border: `1px solid ${COLORS.LIGHT_SECONDARY}40`
  };

  const sectionHeaderStyle = {
    fontFamily: FONT_FAMILY,
    ...TYPOGRAPHY.H4,
    color: COLORS.PRIMARY,
    fontWeight: 700,
    mb: 3,
  };

  return (
    <Box sx={{ ...mainContainerStyle, backgroundColor: "#fafbfc", minHeight: "100%", borderRadius: "20px", p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            color: COLORS.PRIMARY,
            mr: 1,
            "&:hover": { backgroundColor: "rgba(94, 46, 145, 0.04)" }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            ...TYPOGRAPHY.H3,
            color: COLORS.SECONDARY,
            fontWeight: 800,
            fontSize: "28px"
          }}
        >
          {isEditMode ? "Edit Project" : "Create New Project"}
        </Typography>
      </Box>

      <Paper sx={sectionPaperStyle} elevation={0}>
        <Typography sx={sectionHeaderStyle}>1. Basic Details</Typography>
        <Divider sx={{ mb: 3, borderColor: `${COLORS.LIGHT_SECONDARY}40` }} />
        <Box sx={rowContainerStyle}>
        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>Project Name*</Typography>
          <Autocomplete
            value={formData.projectName ? { projectName: formData.projectName } : null}
            onChange={handleProjectSelect}
            filterOptions={filter}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            options={searchableProjects}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              if (option.inputValue) return option.inputValue;
              return option.projectName;
            }}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontWeight: 500 }}>{option.projectName}</Typography>
                  {!option.isNew && (
                    <Typography sx={{ fontSize: '11px', color: COLORS.LIGHT_SECONDARY }}>
                      {option.source} {option.clientName ? `• ${option.clientName}` : ''}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            sx={{
              ...autoGrowStyle,
              "& .MuiAutocomplete-inputRoot": {
                padding: "8px 14px",
                minHeight: "50px",
                "& fieldset": { borderColor: COLORS.GREY_COLOR }
              }
            }}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                multiline
                minRows={1}
                maxRows={5}
                placeholder="Search project or enter new"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {isSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            ListboxProps={{
              style: {
                maxHeight: '200px',
                overflowY: 'auto'
              }
            }}
          />
        </Box>

        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>Address*</Typography>
          <TextField
            multiline
            minRows={1}
            maxRows={5}
            placeholder="Address"
            sx={autoGrowStyle}
            fullWidth
            value={formData.address}
            onChange={(e) => handleChange("address", e.target.value)}
          />
        </Box>

        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>Project Status*</Typography>
          <FormControl fullWidth sx={inputStyle}>
            <Select
              displayEmpty
              MenuProps={menuProps}
              value={formData.projectStatus}
              onChange={(e) => handleChange("projectStatus", e.target.value)}
              renderValue={(selected) =>
                selected ? (
                  <Typography
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.PRIMARY,
                      display: "flex",
                      alignItems: "center",
                      height: "100%"
                    }}
                  >
                    {selected === "completed" ? "Completed" : selected === "inprogress" ? "In Progress" : selected}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: FONT_FAMILY,
                      color: COLORS.LIGHT_SECONDARY,
                      display: "flex",
                      alignItems: "center",
                      height: "100%"
                    }}
                  >
                    Completed / In Progress
                  </Typography>
                )
              }
            >
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="inprogress">In Progress</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {formData.source === "FIR" && (
          <Box sx={columnContainerStyle}>
            <Typography sx={labelStyle}>FIR Status*</Typography>
            <FormControl fullWidth sx={inputStyle}>
              <Select
                displayEmpty
                MenuProps={menuProps}
                value={formData.firStatus || "Requested to Admin"}
                onChange={(e) => handleChange("firStatus", e.target.value)}
              >
                <MenuItem value="Requested to Admin">Requested to Admin</MenuItem>
                <MenuItem value="Project Mapped">Project Mapped</MenuItem>
                <MenuItem value="Assigned Engineer">Assigned Engineer</MenuItem>
                <MenuItem value="Completed Inspection">Completed Inspection</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>
      </Paper>

      <Paper sx={sectionPaperStyle} elevation={0}>
        <Typography sx={sectionHeaderStyle}>2. Client & Manager Info</Typography>
        <Divider sx={{ mb: 3, borderColor: `${COLORS.LIGHT_SECONDARY}40` }} />
        <Box sx={rowContainerStyle}>
        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>Client Name*</Typography>
          <TextField
            fullWidth
            placeholder="Client Name"
            sx={inputStyle}
            value={formData.clientName}
            onChange={(e) => handleChange("clientName", e.target.value)}
          />
        </Box>

        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>Company name / Developer</Typography>
          <TextField
            fullWidth
            placeholder="Developer"
            sx={inputStyle}
            value={formData.companyName}
            onChange={(e) => handleChange("companyName", e.target.value)}
          />
        </Box>

        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>Project Manager</Typography>
          <TextField
            fullWidth
            placeholder="Manager Name"
            sx={inputStyle}
            value={formData.projectManager}
            onChange={(e) => handleChange("projectManager", e.target.value)}
          />
        </Box>
      </Box>

      {/* -------- Row 3 -------- */}

      <Box sx={rowContainerStyle}>
        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>Contact Number*</Typography>
          <TextField
            fullWidth
            placeholder="Contact Number"
            sx={inputStyle}
            value={formData.contactNumber}
            onChange={(e) => handleChange("contactNumber", e.target.value)}
          />
        </Box>

        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>Assigned Engineers</Typography>

          <FormControl fullWidth sx={inputStyle}>
            <Select
              multiple
              displayEmpty
              open={openEngineerMenu}
              onOpen={() => setOpenEngineerMenu(true)}
              onClose={() => setOpenEngineerMenu(false)}
              MenuProps={menuProps}
              value={formData.assignedEngineers}
              onChange={handleEngineerChange}
              renderValue={() => (
                <Typography sx={{
                  fontFamily: FONT_FAMILY,
                  color: COLORS.LIGHT_SECONDARY,
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%'
                }}>
                  Multiselect Engineers List
                </Typography>
              )}
            >
              {engineersList.map((eng) => (
                <MenuItem key={eng._id} value={eng._id}>
                  <Checkbox
                    checked={formData.assignedEngineers.includes(
                      eng._id
                    )}
                  />
                  <ListItemText primary={eng.name} />
                </MenuItem>
              ))}
              {engineersList.length === 0 && !fetchLoading && (
                <MenuItem disabled>No approved engineers found</MenuItem>
              )}
              {fetchLoading && (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading...
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
            {formData.assignedEngineers.map((id) => {
              const eng = engineersList.find((e) => e._id === id);
              // In edit mode, before the list loads, use the name from the pre-loaded project data
              const originalEng = projectToEdit?.assignedEngineers
                ?.find(e => (typeof e === 'string' ? e : e._id) === id);
              const name = eng?.name || (typeof originalEng === 'object' ? originalEng?.name : null) || "Loading...";
              return (
                <Chip
                  key={id}
                  label={name}
                  onDelete={() => handleDelete(id)}
                  sx={{
                    backgroundColor: COLORS.PRIMARY,
                    height: 22,
                    fontFamily: FONT_FAMILY,
                    width: "fit-content",
                    ...TYPOGRAPHY.BODY_STANDARD,
                    color: "#FFF",
                    borderRadius: "5px",
                    "& .MuiChip-deleteIcon": {
                      color: "#FFF !important",
                      backgroundColor: "transparent !important",
                      margin: "0 4px 0 -4px",
                      fontSize: "16px",
                      opacity: 0.8,
                      "&:hover": {
                        color: "#FFF !important",
                        backgroundColor: "transparent !important",
                        opacity: 1
                      }
                    }
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Empty column to maintain 3-column layout */}
        <Box sx={columnContainerStyle}></Box>
      </Box>
      </Paper>

      <Paper sx={sectionPaperStyle} elevation={0}>
        <Typography sx={sectionHeaderStyle}>3. Consultants & Team</Typography>
        <Divider sx={{ mb: 3, borderColor: `${COLORS.LIGHT_SECONDARY}40` }} />
        <Box sx={rowContainerStyle}>
        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>Architect</Typography>
          <TextField
            fullWidth
            placeholder="Architect Name"
            sx={inputStyle}
            value={formData.architect}
            onChange={(e) => handleChange("architect", e.target.value)}
          />
        </Box>

        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>Structural Consultant</Typography>
          <TextField
            fullWidth
            placeholder="Structural Consultant Name"
            sx={inputStyle}
            value={formData.structuralConsultant}
            onChange={(e) => handleChange("structuralConsultant", e.target.value)}
          />
        </Box>

        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>MEP Consultant</Typography>
          <TextField
            fullWidth
            placeholder="MEP Consultant Name"
            sx={inputStyle}
            value={formData.mepConsultant}
            onChange={(e) => handleChange("mepConsultant", e.target.value)}
          />
        </Box>
      </Box>

      {/* -------- Row 5 (More Consultants) -------- */}

      <Box sx={rowContainerStyle}>
        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>Interior Designer</Typography>
          <TextField
            fullWidth
            placeholder="Interior Designer Name"
            sx={inputStyle}
            value={formData.interiorDesigner}
            onChange={(e) => handleChange("interiorDesigner", e.target.value)}
          />
        </Box>

        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>Individual</Typography>
          <TextField
            fullWidth
            placeholder="Individual Name"
            sx={inputStyle}
            value={formData.individual}
            onChange={(e) => handleChange("individual", e.target.value)}
          />
        </Box>

        <Box sx={columnContainerStyle}>
          <Typography sx={labelStyle}>Other Consultant</Typography>
          <TextField
            fullWidth
            placeholder="Other Consultant Name"
            sx={inputStyle}
            value={formData.otherConsultant}
            onChange={(e) => handleChange("otherConsultant", e.target.value)}
          />
        </Box>
      </Box>
      </Paper>

      {(!firRequestToApprove && formData.source !== "FIR") && (
        <Paper sx={sectionPaperStyle} elevation={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography sx={{ ...sectionHeaderStyle, mb: 0 }}>4. Structures / Amenities (Optional)</Typography>
          <Button
            variant="outlined"
            onClick={handleAddTower}
            sx={{
              color: COLORS.PRIMARY,
              borderColor: COLORS.PRIMARY,
              borderRadius: "8px",
              textTransform: "none",
              fontFamily: FONT_FAMILY,
              fontWeight: 600
            }}
          >
            + Add Item
          </Button>
        </Box>
        <Divider sx={{ mb: 3, borderColor: `${COLORS.LIGHT_SECONDARY}40` }} />
        
        {(formData.towers || []).length === 0 ? (
          <Typography sx={{ color: COLORS.LIGHT_SECONDARY, fontFamily: FONT_FAMILY, fontStyle: 'italic' }}>
            No items added. Leave empty if this project does not have multiple structures, amenities, etc.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {(formData.towers || []).map((tower, idx) => {
              const isStandardType = !tower.projectType || tower.projectType === "Custom Mapping" || masterMappings.some(m => m.mappingName.toLowerCase() === tower.projectType.toLowerCase());
              const displayProjectType = isStandardType ? tower.projectType : "Custom Mapping";
              const displayCustomTypeName = isStandardType ? tower.customTypeName : tower.projectType;

              return (
              <Box key={idx} sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: "100%", sm: "repeat(2, 1fr)", md: displayProjectType === 'Custom Mapping' ? "1fr 1fr 1fr auto" : "1fr 1fr auto" }, 
                gap: "24px", 
                position: 'relative' 
              }}>
                <Box sx={columnContainerStyle}>
                  <Typography sx={labelStyle}>Item Name*</Typography>
                  <TextField
                    fullWidth
                    placeholder="e.g. Tower A, Clubhouse, Podium"
                    sx={inputStyle}
                    value={tower.name}
                    onChange={(e) => handleTowerChange(idx, "name", e.target.value)}
                  />
                </Box>
                <Box sx={columnContainerStyle}>
                  <Typography sx={labelStyle}>Mapping / Type*</Typography>
                  <FormControl fullWidth sx={inputStyle}>
                    <Select
                      displayEmpty
                      MenuProps={menuProps}
                      value={displayProjectType}
                      onChange={(e) => {
                        const val = e.target.value;
                        handleTowerChange(idx, "projectType", val);
                        if (val !== "Custom Mapping") {
                          handleTowerChange(idx, "customTypeName", "");
                        }
                      }}
                    >
                      <MenuItem value="" disabled>Select Type</MenuItem>
                      {masterMappings.map((master) => (
                        <MenuItem key={master._id} value={master.mappingName}>
                          {master.mappingName.charAt(0).toUpperCase() + master.mappingName.slice(1)}
                        </MenuItem>
                      ))}
                      <MenuItem value="Custom Mapping">Custom Mapping</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {displayProjectType === 'Custom Mapping' && (
                  <Box sx={columnContainerStyle}>
                    <Typography sx={labelStyle}>Custom Type Name*</Typography>
                    <TextField
                      fullWidth
                      placeholder="e.g. Villa, Farmhouse"
                      sx={inputStyle}
                      value={displayCustomTypeName || ""}
                      onChange={(e) => {
                        handleTowerChange(idx, "customTypeName", e.target.value);
                        if (!isStandardType) {
                          handleTowerChange(idx, "projectType", "Custom Mapping");
                        }
                      }}
                    />
                  </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "center", pt: 3 }}>
                  <IconButton onClick={() => handleRemoveTower(idx)} sx={{ color: COLORS.ERROR }}>
                    <Icon name="delete" />
                  </IconButton>
                </Box>
              </Box>
            )})}
          </Box>
        )}
      </Paper>
      )}

      {/* -------- Button -------- */}

      <Box sx={{ 
        display: "flex", 
        flexDirection: { xs: "column-reverse", sm: "row" }, 
        justifyContent: "flex-end", 
        mt: 2, 
        mb: 4, 
        gap: 2 
      }}>
        {!isEditMode && (
          <Button
            variant="outlined"
            startIcon={<Icon name="delete" />}
            onClick={() => {
              clearDraft();
              showToast("Form fields cleared successfully", "info");
              setTimeout(() => window.location.reload(), 1000);
            }}
            sx={{
              color: COLORS.ERROR,
              borderColor: COLORS.ERROR,
              borderRadius: "10px",
              px: 4,
              py: 1.5,
              textTransform: "none",
              fontFamily: FONT_FAMILY,
              ...TYPOGRAPHY.H3,
              width: { xs: "100%", sm: "auto" },
              "&:hover": {
                borderColor: COLORS.ERROR,
                backgroundColor: "rgba(239, 68, 68, 0.08)"
              }
            }}
          >
            Clear Fields
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            backgroundColor: COLORS.SECONDARY,
            borderRadius: "10px",
            px: 5,
            py: 1.5,
            textTransform: "none",
            fontFamily: FONT_FAMILY,
            ...TYPOGRAPHY.H3,
            width: { xs: "100%", sm: "auto" },
            "&:hover": { backgroundColor: COLORS.PRIMARY }
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : isEditMode ? (
            "Update Project"
          ) : (
            "Create Project & Start Mapping"
          )}
        </Button>
      </Box>

      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default AddNewProject;